// Developer: Ankit Gupta
/**
 * Orion, docked on every DataPilot page.
 *
 * The full Orion screen is for long work. This is the same conversation in a
 * side panel, opened from the top bar on whatever page the person is on, and
 * started with what that page is about so they do not have to say it.
 */
import { lazy, Suspense, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Drawer, Space, Spin, Tooltip } from 'antd';
import { ExpandAltOutlined, PlusOutlined } from '@ant-design/icons';
import { useLanguage, useT } from '@/i18n';
import { chatApi } from './api';
import { useWaitingApprovals } from './Approvals';
import { OrionIcon } from './OrionIcon';
import './chat.css';

// The conversation itself is loaded when the panel is first opened, so the shell of every
// page does not carry the whole Orion screen.
const DockBody = lazy(() => import('./DockBody'));

/** What the page is about, as the opening words of a request.
 *  These words are put into the person's own message and sent as it, so they are left as they are
 *  in both languages; the person can rewrite them before sending. */
function contextFor(pathname: string): string {
  const run = /^\/projects\/(\d+)\/runs\/(\d+)/.exec(pathname);
  if (run) return `About run ${run[2]} of project ${run[1]}: `;
  const project = /^\/projects\/(\d+)/.exec(pathname);
  if (project) return `About project ${project[1]}: `;
  if (pathname.startsWith('/reconciliation')) return 'About reconciliation in this project: ';
  if (pathname.startsWith('/runs')) return 'About the migration runs of this project: ';
  if (pathname.startsWith('/scheduled-runs')) return 'About the scheduled runs of this project: ';
  return '';
}

export function OrionDock() {
  const { t } = useT('chat');
  const rtl = useLanguage() === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<number | null>(null);
  const channels = useQuery({ queryKey: ['chat-channels'], queryFn: () => chatApi.channels(), staleTime: 15_000, enabled: open });
  const waiting = useWaitingApprovals();
  const count = (waiting.data ?? []).length;
  if (location.pathname.startsWith('/orion')) return null; // the full screen is already open
  const list = channels.data ?? [];
  return (
    <>
      <Tooltip title={count > 0 ? t('dock.tipWaiting', { count }) : t('dock.tip')}>
        <button type="button" className="dp-pill dp-pill-button dp-pill-orion" onClick={() => setOpen(true)} aria-label={t('dock.aria')}>
          <Badge count={count} size="small" offset={[rtl ? -4 : 4, -2]}><OrionIcon size={18} /></Badge>
          <span className="dp-pill-value">Orion</span>
        </button>
      </Tooltip>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width={620}
        placement={rtl ? 'left' : 'right'}
        className="orion-dock"
        title={<Space><OrionIcon size={20} /> Orion</Space>}
        extra={
          <Space>
            {count > 0 && <Button size="small" type="primary" onClick={() => { setOpen(false); navigate('/orion/approvals'); }}>{t('dock.toApprove', { count })}</Button>}
            {threadId && <Button size="small" icon={<PlusOutlined />} onClick={() => setThreadId(null)}>{t('dock.new')}</Button>}
            <Tooltip title={t('dock.openFull')}>
              <Button size="small" icon={<ExpandAltOutlined />} onClick={() => { setOpen(false); navigate(threadId ? `/orion/${threadId}` : '/orion'); }} />
            </Tooltip>
          </Space>
        }
        destroyOnClose={false}
      >
        {!open ? null : channels.isLoading ? (
          <div className="chat-empty"><Spin /></div>
        ) : list.length === 0 ? (
          <div className="chat-empty"><h3>{t('dock.noAssistant')}</h3><Button onClick={() => { setOpen(false); navigate('/orion/applications'); }}>{t('dock.applications')}</Button></div>
        ) : (
          <Suspense fallback={<div className="chat-empty"><Spin /></div>}>
            <DockBody channels={list} threadId={threadId} setThreadId={setThreadId} pathname={location.pathname} initialText={contextFor(location.pathname)} />
          </Suspense>
        )}
      </Drawer>
    </>
  );
}

export default OrionDock;
